import { FC, Fragment, useEffect, useState } from 'react';
import {
	Avatar,
	Button,
	Form,
	Modal,
	Row,
	Space,
	Switch,
	Table,
	Tooltip,
	Typography,
} from 'antd';
import {
	InfoCircleOutlined,
	PlusOutlined,
	ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/App';
import CardPanel from '@/components/CardPanel';
import { FieldType, renderField } from '@/components/FormBuilder';
import formRule from '@/libs/antd/rules';
import {
	UserGroup,
	UserGroupPayload,
	createUserGroup,
	getUserGroups,
	updateUserGroup,
} from '@/libs/fleet-api/services/user-group';
import { getUsers } from '@/libs/fleet-api/services/users';
import { getInitialTheme } from '@/libs/antd/theme';
import { getMasterGroups } from '@/libs/fleet-api/services/master-group';

const USER_GROUP_FORMS = [
	{
		name: 'masterGroupId',
		type: 'select',
		label: 'Master Group',
		config: {
			placeholder: 'Select Master Group',
			allowClear: true,
			options: [],
		},
		rules: [formRule.required()],
	},
	{
		name: 'masterUserId',
		type: 'select',
		label: 'User Group',
		config: {
			placeholder: 'Select User Group',
			allowClear: true,
			options: [],
		},
		rules: [formRule.required()],
	},
	{
		name: 'isActive',
		type: 'switch',
		label: 'Status',
		width: '1/2',
		config: {
			checkedChildren: 'Active',
			unCheckedChildren: 'Inactive',
		},
	},
] as FieldType[];

const UserGroupPage: FC = () => {
	const { notification } = useApp();
	const { data, isLoading, isFetching, refetch } = useQuery({
		queryKey: ['getUserGroups'],
		queryFn: () => getUserGroups(),
	});

	const dataSource =
		data?.data.map((value, key) => {
			return {
				...value,
				key,
			};
		}) ?? [];

	const [modalDetailForm] = Form.useForm<UserGroupPayload>();
	const [modalDetail, setModalDetail] = useState<
		| {
				type: 'create';
		  }
		| {
				type: 'update';
				data: UserGroup;
		  }
	>();

	useEffect(() => {
		if (modalDetail?.type !== 'update') return;
		modalDetailForm.setFieldsValue(modalDetail.data);
	}, [modalDetail, modalDetailForm]);

	const users = useQuery({
		queryKey: ['getUsers'],
		queryFn: () => getUsers(),
	});

	const masterGroups = useQuery({
		queryKey: ['getMasterGroups'],
		queryFn: () => getMasterGroups(),
	});

	return (
		<Fragment>
			<CardPanel
				title="User Group"
				extra={
					<Space size="small">
						<Button
							icon={<ReloadOutlined />}
							loading={isFetching}
							onClick={() => refetch()}
						>
							Refresh
						</Button>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => {
								setModalDetail({ type: 'create' });
							}}
						>
							New Entry
						</Button>
					</Space>
				}
				styles={{
					header: {
						paddingRight: 12,
					},
				}}
			>
				<Table
					dataSource={dataSource}
					loading={isLoading || isFetching}
					columns={[
						{
							title: '#',
							align: 'center',
							width: 60,
							render: (_, __, index) => {
								return index + 1;
							},
						},
						{
							title: 'Group ID',
							ellipsis: true,
							render: (_, record) => (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										setModalDetail({
											type: 'update',
											data: record,
										});
									}}
								>
									{record.masterGroupId}
								</a>
							),
						},
						Table.EXPAND_COLUMN,
						{
							title: 'User ID',
							ellipsis: true,
							render: (_, record) => record.masterUserId,
						},
						{
							title: 'Status',
							align: 'center',
							dataIndex: 'isActive',
							render: (isActive) => (
								<Switch
									value={isActive}
									checkedChildren="Active"
									unCheckedChildren="Inactive"
									style={{ cursor: 'default' }}
								/>
							),
						},
						{
							title: 'Created',
							align: 'center',
							dataIndex: 'createdDate',
							render: (createdDate) => {
								const date = dayjs(createdDate);
								const shortDate = date.format('DD-MM-YYYY');
								const longDate = date.format('DD MMMM YYYY, HH:mm');

								return (
									<Space>
										<span>{shortDate}</span>
										<Tooltip title={longDate}>
											<InfoCircleOutlined />
										</Tooltip>
									</Space>
								);
							},
						},
						{
							title: 'Updated',
							align: 'center',
							dataIndex: 'createdDate',
							render: (createdDate) => {
								const date = dayjs(createdDate);
								const shortDate = date.format('DD-MM-YYYY');
								const longDate = date.format('DD MMMM YYYY, HH:mm');

								return (
									<Space>
										<span>{shortDate}</span>
										<Tooltip title={longDate}>
											<InfoCircleOutlined />
										</Tooltip>
									</Space>
								);
							},
						},
					]}
					expandable={{
						expandedRowRender(record) {
							return (
								<div style={{ padding: 16 }}>
									<pre style={{ margin: 0 }}>
										{JSON.stringify(record, null, 2)}
									</pre>
								</div>
							);
						},
					}}
				/>
			</CardPanel>

			<Modal
				open={Boolean(modalDetail)}
				width={500}
				title={modalDetail?.type === 'update' ? 'Edit' : 'New'}
				afterClose={() => modalDetailForm.resetFields()}
				onCancel={() => setModalDetail(undefined)}
				onOk={() => modalDetailForm.submit()}
			>
				<Form
					form={modalDetailForm}
					layout="vertical"
					onFinish={async (values) => {
						try {
							if (modalDetail?.type === 'update') {
								await updateUserGroup(modalDetail.data.id, values);
								notification.success({
									message: 'Updated',
									description: 'data was successfully updated',
								});
							} else {
								await createUserGroup(values);
								notification.success({
									message: 'Created',
									description: 'data was successfully created',
								});
							}
							setModalDetail(undefined);
							refetch();
						} catch (error) {
							console.log(error);
						}
					}}
				>
					<Row gutter={[16, 16]}>
						{USER_GROUP_FORMS.map((field) => {
							if (field.type === 'select' && field.name === 'masterGroupId') {
								field.config!.options = masterGroups.data?.data.map((group) => {
									return {
										value: group.id,
										label: group.name,
									};
								});
							}

							if (field.type === 'select' && field.name === 'masterUserId') {
								field.config!.options = users.data?.data.map((user) => {
									return {
										value: user.userId,
										label: user.fullName,
										userName: user.userName,
										fullName: user.fullName,
									};
								});

								field.config!.optionRender = (option) => {
									const userAvatar = getInitialTheme(option.data.fullName);
									return (
										<Space>
											<Avatar
												size={32}
												style={userAvatar.style}
											>
												{userAvatar.initial}
											</Avatar>
											<div>
												<div>{option.data.label}</div>
												<Typography.Text
													type="secondary"
													style={{ fontWeight: 'normal' }}
												>
													{option.data.userName}
												</Typography.Text>
											</div>
										</Space>
									);
								};
							}

							return field;
						}).map((field, key) => renderField({ ...field, key }))}
					</Row>
				</Form>
			</Modal>
		</Fragment>
	);
};

export default UserGroupPage;
